<template>
    <div class="tg-page-root min-h-screen bg-gray-50 dark:bg-gray-950">
        <PageHeader :title="$t('PageTitle_My_Invitations')" @back="goBack" />
        <div class="py-7 mx-auto px-[5%]">
            <div class="pb-3 text-lg text-gray-800 dark:text-gray-100 font-bold">{{ $t('Invitation_Rules_Title') }}</div>
            <div class="grid grid-cols-2 gap-3 mb-6">
                <div v-for="(rule, index) in rules" :key="index" class="relative bg-white dark:bg-gray-900 border border-brand/20 dark:border-brand/25 rounded-lg h-24 flex overflow-hidden shadow-sm dark:shadow-none">
                    <!-- Left side vertical text -->
                    <div class="w-8 shrink-0 self-stretch flex items-center justify-center bg-brand/5 dark:bg-brand/10 border-r border-brand/10 dark:border-brand/20">
                        <span class="text-[10px] text-brand dark:text-violet-300 invite-side-label font-medium">{{ $t('Receive_Text') }}</span>
                    </div>
                    
                    <!-- Right side content -->
                    <div class="flex-1 min-w-0 flex flex-col items-center justify-center p-1 text-center">
                        <div class="w-full text-[10px] text-gray-500 dark:text-gray-300 mb-0.5 leading-tight">{{ $t('Invite_Friends_Count', rule.count, { count: rule.count }) }}</div>
                        <div class="w-full text-xl font-bold text-brand dark:text-violet-300 leading-tight">{{ $t('Receive_Coupon_Value', { value: rule.discount }) }}</div>
                        <div class="w-full text-[10px] text-brand/80 dark:text-violet-300/80 mt-0.5">{{ $t('Coupon_Text') }}</div>
                    </div>

                    <!-- Ticket circles -->
                    <div class="absolute left-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-50 dark:bg-gray-950 rounded-full border-r border-brand/20 dark:border-brand/25"></div>
                    <div class="absolute right-[-6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-50 dark:bg-gray-950 rounded-full border-l border-brand/20 dark:border-brand/25"></div>
                </div>
            </div>

            <div class="bg-white dark:bg-gray-900 rounded-xl p-4 flex items-center justify-between shadow-sm dark:shadow-none border border-transparent dark:border-gray-800 mb-8">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-brand/5 dark:bg-brand/10 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-brand opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                        </svg>
                    </div>
                    <div>
                        <div class="text-xs text-gray-500 dark:text-gray-300 mb-0.5">{{ $t('My_Invitation_Code') }}</div>
                        <div class="font-bold text-lg tracking-wide text-gray-900 dark:text-gray-100">{{ invitationCode || '-' }}</div>
                    </div>
                </div>
                <div class="text-brand dark:text-violet-300 font-semibold text-sm cursor-pointer hover:opacity-80 px-2 py-1" @click="copyText">
                    {{ $t('Copy') }}
                </div>
            </div>

            <div class="w-full py-3.5 rounded-xl bg-brand text-white text-center font-semibold text-lg cursor-pointer shadow-md shadow-brand/20 dark:shadow-none hover:opacity-90 transition-opacity"
                @click="goInvite">{{ $t('Invite_Now') }}
            </div>

            <!-- Invited Users List -->
            <div class="mt-8">
                <div class="pb-3 text-lg text-brand dark:text-violet-300 font-semibold">{{ $t('Invited_Users') }}</div>
                <div class="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-sm dark:shadow-none border border-transparent dark:border-gray-800">
                    <div v-if="invitees.length > 0">
                        <div class="grid grid-cols-2 gap-4 text-xs text-gray-400 dark:text-gray-300 mb-2 border-b border-gray-100 dark:border-gray-800 pb-2">
                            <div>{{ $t('Invited_User_Email') }}</div>
                            <div class="text-right">{{ $t('Invited_User_Date') }}</div>
                        </div>
                        <div v-for="(user, index) in invitees" :key="index" class="grid grid-cols-2 gap-4 text-sm py-2 border-b border-gray-50 dark:border-gray-800/70 last:border-0">
                            <div class="font-medium text-gray-700 dark:text-gray-100 truncate">{{ user.UserName || user.Email }}</div>
                            <div class="text-right text-gray-500 dark:text-gray-300">{{ formatDate(user.CreateTime) }}</div>
                        </div>
                    </div>
                    <div v-else class="text-center py-8 text-gray-400 dark:text-gray-300 text-sm">
                        {{ $t('No_Invited_Users') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router'
import { showAppNotification } from '@/utils/appNotification'
import { writeAppClipboard } from '@/utils/appClipboard'
import { getInvitationCode, setInvitationCode } from '@/utils/invitationCodeStorage'
import { buildReferralDeepLink } from '@/utils/referralDeepLink'
import logger from '@/utils/logger'
import PageHeader from '@/components/PageHeader.vue'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'
import { authApi } from '@/api/auth'

const { shareViaTelegram } = useTelegramWebApp()

const router = useRouter()

const goBack = () => {
    router.back()
}

const { t } = useI18n();
const invitationCode = ref('')
const invitees = ref([])

const rules = [
    { count: 1, discount: 10 },
    { count: 2, discount: 20 },
    { count: 3, discount: 30 },
    { count: 4, discount: 50 },
]

const copyText = async () => {
    if (!invitationCode.value) return
    logger.debug('invitationCode:', invitationCode.value)
    const copied = await writeAppClipboard(invitationCode.value)
    if (!copied) return
    showAppNotification({ message: t('Copied_Successfully'), type: 'success', position: 'bottom-right', duration: 2000 })
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString()
    } catch (e) {
        return dateStr
    }
}

onMounted(async () => {
    invitationCode.value = await getInvitationCode('')

    try {
        const res = await authApi.getUserInfo();
        if (res.data && res.data.data) {
            if (res.data.data.InvitationCode) {
                invitationCode.value = res.data.data.InvitationCode;
                await setInvitationCode(invitationCode.value);
            }
        }
        
        // Fetch invitees
        const inviteesRes = await authApi.getInvitees();
        if (inviteesRes.data && inviteesRes.data.status === '0') {
            invitees.value = inviteesRes.data.data;
        }
    } catch (error) {
        logger.error('Failed to fetch user info', error);
    }
})

const goInvite = async () => {
    const title = t('Share_Title');
    const code = invitationCode.value || await getInvitationCode('');
    const url = buildReferralDeepLink(code);
    
    const text = t('Share_Content', { code, url });

    logger.debug('Share text:', text)

    if (shareViaTelegram({ url, text })) {
        return
    }

    try {
        await navigator.share({
            title,
            text,
            url
        });
    } catch (error) {
        const copied = await writeAppClipboard(text)
        if (copied) {
            showAppNotification({ message: t('Copied_Successfully'), type: 'success', position: 'bottom-right', duration: 2000 });
        }
    }
}



</script>

<style scoped>
.invite-side-label {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    line-height: 1;
}
</style>
